import { AppNS } from 'app';
import { StaffDisciplineData } from 'app/api_v2/agency/disciplines';
import { Params } from 'app/types';
import * as Yup from 'yup';

import { Box } from '@mui/material';

export interface FormValues {
  discipline: AppNS.DisciplineId;
}

export const defaultValues: FormValues = {
  discipline: '' as AppNS.DisciplineId,
};

const detailsFields = {
  discipline: Yup.string().required('Please pick  discipline'),
};

export const getValidationSchema = () => {
  return Yup.object({
    ...detailsFields,
  });
};

export const getDisciplinesByParents = (parents: string[]): Params[] => {
  return DISCIPLINES.filter((discipline) => parents.includes(discipline.parent));
};

export const getParentsByDisciplines = (disciplines: AppNS.DisciplineId[]): AppNS.DisciplineAgencyType[] => {
  const types = DISCIPLINES.filter((discipline) => disciplines.includes(discipline.id)).map(
    (discipline) => discipline.parent,
  );
  return [...new Set(types)];
};

export const getDisciplinesForList = (excludes: string[], allowed: string[]): Params[] => {
  let current = '';
  return DISCIPLINES.filter(
    (discipline) =>
      allowed.includes(discipline.id) ||
      (allowed.includes('specialInspectionsAll') && discipline.parent === 'specialInspections'),
  )
    .filter((discipline) => !excludes.includes(discipline.id))
    .reduce((acc: Params[], discipline) => {
      const type = getTypeById(discipline.parent);
      if (current !== discipline.parent) {
        acc.push({ id: 'optgroup', name: type?.name || '' });
        current = discipline.parent;
      }
      acc.push(discipline);
      return acc;
    }, []);
};

export const getDisciplinesById = (id: string): DisciplineData | undefined => {
  return DISCIPLINES.find((discipline) => discipline.id === id);
};

export const getTypeById = (id: string): Params | undefined => {
  return AGENCY_TYPES.find((discipline) => discipline.id === id);
};

export const AGENCY_TYPES: { id: AppNS.DisciplineAgencyType; name: string }[] = [
  { id: 'regulatoryCode', name: 'Regulatory Inspections' },
  { id: 'planReview', name: 'Plan Review' },
  { id: 'elevatorInspection', name: 'Elevator Inspection' },
  { id: 'commercialBoiler', name: 'Commercial Boiler' },
  { id: 'specialInspections', name: 'Special Inspections' },
  { id: 'greenBuilding', name: 'Green Building' },
  { id: 'doee', name: 'DOEE' },
];

export interface DisciplineData {
  id: AppNS.DisciplineId;
  name: string;
  parent: AppNS.DisciplineAgencyType;
  staffData?: StaffDisciplineData | null;
  state?: AppNS.CompanyDisciplineState;
}

export const DISCIPLINES: DisciplineData[] = [
  {
    id: 'commercialBuildingRegulatoryCode',
    name: 'Commercial Building Inspection',
    parent: 'regulatoryCode',
  },
  {
    id: 'commercialElectricalRegulatoryCode',
    name: 'Commercial Electrical Inspection',
    parent: 'regulatoryCode',
  },
  {
    id: 'commercialMechanicalRegulatoryCode',
    name: 'Commercial Mechanical Inspection',
    parent: 'regulatoryCode',
  },
  {
    id: 'commercialPlumbingRegulatoryCode',
    name: 'Commercial Plumbing Inspection',
    parent: 'regulatoryCode',
  },
  {
    id: 'residentialBuildingRegulatoryCode',
    name: 'Residential Building Inspection',
    parent: 'regulatoryCode',
  },
  { id: 'fireProtectionRegulatoryCode', name: 'Fire Protection Inspection', parent: 'regulatoryCode' },
  {
    id: 'residentialEnergyRegulatoryCode',
    name: 'Residential Energy Inspection',
    parent: 'regulatoryCode',
  },
  { id: 'commercialEnergyRegulatoryCode', name: 'Commercial Energy Inspection', parent: 'regulatoryCode' },
  { id: 'greenCodeRegulatoryCode', name: 'Green Code Inspection', parent: 'regulatoryCode' },
  { id: 'fuelGas', name: 'Fuel Gas', parent: 'regulatoryCode' },

  { id: 'commercialBuildingPlanReview', name: 'Commercial Building Plan Review', parent: 'planReview' },
  { id: 'commercialElectricalPlanReview', name: 'Commercial Electrical Plan Review', parent: 'planReview' },
  { id: 'commercialMechanicalPlanReview', name: 'Commercial Mechanical Plan Review', parent: 'planReview' },
  { id: 'commercialPlumbingPlanReview', name: 'Commercial Plumbing Plan Review', parent: 'planReview' },
  { id: 'residentialBuildingPlanReview', name: 'Residential Building Plan Review', parent: 'planReview' },
  { id: 'fireProtectionPlanReview', name: 'Fire Protection Plan Review', parent: 'planReview' },
  { id: 'residentialEnergyPlanReview', name: 'Residential Energy Plan Review', parent: 'planReview' },
  { id: 'commercialEnergyPlanReview', name: 'Commercial Energy Plan Review', parent: 'planReview' },
  { id: 'greenCodePlanReview', name: 'Green Code Plan Review', parent: 'planReview' },

  { id: 'elevator', name: 'Elevator Inspection', parent: 'elevatorInspection' },
  { id: 'elevatorPlanReview', name: 'Elevator Plan Review', parent: 'elevatorInspection' },

  { id: 'boilerWelder', name: 'Boiler Welder', parent: 'commercialBoiler' },
  { id: 'boilerInspection', name: 'Boiler Inspection', parent: 'commercialBoiler' },

  { id: 'buildingCommissioning', name: 'Building Commissioner', parent: 'greenBuilding' },
  { id: 'buildingLeakage', name: 'Building Leakage Tester', parent: 'greenBuilding' },

  { id: 'specialInspectionsAll', name: 'Special Inspection PIC', parent: 'specialInspections' },
  {
    id: 'prestressedPostTensionedConcrete',
    name: 'Pre-stressed/Post-tensioned Concrete',
    parent: 'specialInspections',
  },
  { id: 'reinforcedConcrete', name: 'Reinforced Concrete', parent: 'specialInspections' },

  // changed name but id need to be the same to not break existing data
  { id: 'reinforcedMasonry', name: 'Structural Masonry', parent: 'specialInspections' },

  { id: 'structuralConcrete', name: 'Structural Concrete', parent: 'specialInspections' },

  { id: 'welding', name: 'Welding', parent: 'specialInspections' },
  { id: 'precastConcrete', name: 'Precast Concrete', parent: 'specialInspections' },
  { id: 'structuralSteel', name: 'Structural Steel', parent: 'specialInspections' },
  { id: 'sprayAppliedFireproofing', name: 'Spray Applied Fireproofing', parent: 'specialInspections' },
  { id: 'smokeControlSystems', name: 'Smoke control systems', parent: 'specialInspections' },
  {
    id: 'masticIntumescentFireResistantCoatings',
    name: 'Mastic and Intumescent Fire-resistant coatings',
    parent: 'specialInspections',
  },
  {
    id: 'eifsInstallation',
    name: 'Exterior Insulation and Finish Systems (EiFs) Instalation',
    parent: 'specialInspections',
  },
  { id: 'pierPileFoundations', name: 'Pier and Pile Foundations', parent: 'specialInspections' },
  {
    id: 'postInstalledStructuralAnchors',
    name: 'Post-installed Structural Anchors in Concrete',
    parent: 'specialInspections',
  },
  { id: 'soils', name: 'Soils', parent: 'specialInspections' },
  { id: 'structuralWood', name: 'Structural Wood', parent: 'specialInspections' },
  { id: 'foundation', name: 'Foundation', parent: 'specialInspections' },
  { id: 'fireStopping', name: 'Fire Stopping', parent: 'specialInspections' },
  { id: 'concreteTesting', name: 'Concrete testing', parent: 'specialInspections' },
  { id: 'doee', name: 'DOEE', parent: 'doee' },
];

const CLASSES: Record<AppNS.CompanyDisciplineState, string> = {
  draft: 'outlined-gray flat',
  pending: 'warning flat',
  approved: 'outlined-darkblue',
  deactivated: 'error-outlined flat',
  revoked: 'error-outlined flat',
  disabled: 'error-outlined flat',
};

const DISCIPLINE_STATE: Record<AppNS.CompanyDisciplineState, string> = {
  draft: 'Draft',
  pending: 'Proposed',
  approved: 'Recognized',
  deactivated: 'Inactive',
  revoked: 'Revoked',
  disabled: 'Disabled',
};

export const getListData = (data: DisciplineData[]) => {
  return data.map((item) => {
    return {
      id: item.id,
      name: item.name,
      tooltip: (
        <Box p={0.5} fontWeight="normal">
          Discipline status: <strong>{getStateName(item.state || 'draft')}</strong>
        </Box>
      ),
      classNames: getClass(item.state || 'draft'),
    };
  });
};

const getClass = (state: AppNS.CompanyDisciplineState): string => {
  return CLASSES[state] || 'warning flat';
};

const getStateName = (state: AppNS.CompanyDisciplineState): string => {
  return DISCIPLINE_STATE[state] || 'Draft';
};

const DISCIPLIES_TO_ISPECTION_GROUPS_MAP: Record<string, AppNS.DisciplineId[]> = {
  BUILDING: ['commercialBuildingRegulatoryCode', 'residentialBuildingRegulatoryCode', 'greenCodeRegulatoryCode'],
  HISTORIC: ['commercialBuildingRegulatoryCode', 'residentialBuildingRegulatoryCode', 'greenCodeRegulatoryCode'],
  ENERGY: ['residentialEnergyRegulatoryCode', 'commercialEnergyRegulatoryCode'],
  FIRE: ['fireProtectionRegulatoryCode'],
  ELECTRICAL: ['commercialElectricalRegulatoryCode'],
  MECHANICAL: ['commercialMechanicalRegulatoryCode'],
  PLUMBING: ['commercialPlumbingRegulatoryCode'],
  DOEE: ['doee'],
  SPECIAL: [
    'specialInspectionsAll',
    'prestressedPostTensionedConcrete',
    'reinforcedConcrete',
    'reinforcedMasonry',
    'welding',
    'precastConcrete',
    'structuralSteel',
    'sprayAppliedFireproofing',
    'smokeControlSystems',
    'masticIntumescentFireResistantCoatings',
    'eifsInstallation',
    'pierPileFoundations',
    'postInstalledStructuralAnchors',
    'soils',
    'structuralWood',
    'foundation',
    'fireStopping',
    'concreteTesting',
  ],
  'PLAN REVIEW': [
    'commercialBuildingPlanReview',
    'residentialBuildingPlanReview',
    'commercialElectricalPlanReview',
    'commercialMechanicalPlanReview',
    'commercialPlumbingPlanReview',
    'fireProtectionPlanReview',
    'residentialEnergyPlanReview',
    'commercialEnergyPlanReview',
    'greenCodePlanReview',
  ],
  GREEN: ['buildingCommissioning', 'buildingLeakage'],
};

export const getInspectionGroupsByDisciplines = (disciplines: AppNS.DisciplineId[]): string[] => {
  const groups: string[] = [];
  Object.keys(DISCIPLIES_TO_ISPECTION_GROUPS_MAP).forEach((groupKey) => {
    const mappedDisciplines = DISCIPLIES_TO_ISPECTION_GROUPS_MAP[groupKey];
    if (disciplines.some((discipline) => mappedDisciplines.includes(discipline))) {
      groups.push(groupKey);
    }
  });
  return groups;
};
